import { memo, ReactElement, ReactNode } from 'react'

import { ExclamationOutlined } from '@ant-design/icons'
import { FormikProps } from 'formik'
import isEqual from 'lodash/isEqual'
import { useTranslation } from 'react-i18next'

import { IconButton } from 'components/Buttons'
import { Flex, Icon } from 'components/core'
import { useJustSaved } from 'hooks'

interface Props<T> {
    field?: ReactElement
    handleSubmit: FormikProps<T>['handleSubmit']
    handleCancel: () => void
    isSubmitting: FormikProps<T>['isSubmitting']
    disableSubmit?: boolean
    isValid: FormikProps<T>['isValid']
    submitFailure: boolean
    useApplyAndCancelButtons?: boolean
    extraContent?: ReactNode
}

function SingleValueFieldBody<T>({
    field,
    handleSubmit,
    handleCancel,
    isSubmitting,
    disableSubmit = false,
    isValid,
    submitFailure,
    useApplyAndCancelButtons = true,
    extraContent,
}: Props<T>): ReactElement {
    const justSaved = useJustSaved(isSubmitting)
    const { t } = useTranslation(['common'])

    const generateIcon = (): ReactElement => {
        const justSavedIcon = submitFailure ? (
            <ExclamationOutlined />
        ) : (
            <Icon name="CHECKMARK" />
        )
        return justSaved ? justSavedIcon : <Icon name="CHECKMARK" />
    }

    const justFailed = justSaved && submitFailure

    return (
        <>
            <Flex justifyContent="center">
                {field}
                {useApplyAndCancelButtons && (
                    <>
                        <IconButton
                            size="small"
                            type={justFailed ? 'link' : 'primary'}
                            danger={justFailed}
                            style={{ marginLeft: 8 }}
                            icon={generateIcon()}
                            tooltipTitle={t('common:save', 'Save')}
                            disabled={!isValid || disableSubmit}
                            loading={isSubmitting}
                            onClick={() => handleSubmit()}
                        />
                        <IconButton
                            size="small"
                            style={{ marginLeft: 4 }}
                            icon={<Icon name="PLUS" rotate={45} />}
                            tooltipTitle={t('common:cancel', 'Cancel')}
                            disabled={isSubmitting}
                            onClick={handleCancel}
                        />
                    </>
                )}
            </Flex>
            {extraContent}
        </>
    )
}

export default memo(
    SingleValueFieldBody,
    isEqual
) as typeof SingleValueFieldBody
